import * as React from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAtomValue } from 'jotai';

import { theme } from '../../../theme/theme';
import { selectTeethAtom } from '../../patientList/atoms';
import { ToothLabel } from '../casePresentation/toolbar/CasePresentationImageTracker';
import { showSegmentationAtom } from '../multiFileViewer/imageToolbar/atoms';
import RerunAiButton from '../multiFileViewer/RerunAiButton';
import { useRotation } from '../multiFileViewer/RotationContext';
import type { ImageObject } from '../types';

import { ManageFindings } from './ManageFindings/ManageFindings';

type EventHandler = () => void;

type Props = {
  currentImageFilter: string;
  currentImageIndex?: number;
  totalImages?: number;
  onPreviousClick: EventHandler;
  onNextClick: EventHandler;
  activeImage?: ImageObject;
  showDisclaimer?: boolean;
};

const useStyles = makeStyles({
  imageNavigationBox: () => ({
    display: 'flex',
    alignItems: 'center',
    '& > button': {
      color: theme.palette.primary.contrastText,
      padding: '0 10px',
    },
  }),
  imageIndexText: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1em',
    position: 'relative',
    lineHeight: '2em',
    fontSize: '1em',
    color: theme.palette.primary.contrastText,
    zIndex: 100,
    whiteSpace: 'nowrap',
    padding: '0 0 0 0.5em',
  },
  imageFilterLabel: {
    margin: '0 0.5em 0 0.5em',
  },
  button: {
    position: 'relative',
    height: '2em',
    lineHeight: '2em',
    fontSize: '1em',
    padding: '0 1em 0 2.5em',
    zIndex: 100,
    color: theme.palette.primary.light,

    '&:nth-of-type(2)': {
      paddingLeft: 15,
      marginLeft: -5,
    },
    '& > span': {
      display: 'inlineBlock',
      position: 'relative',
      zIndex: 5,
      whiteSpace: 'nowrap',
    },
    '& > svg': {
      verticalAlign: 'middle',
      color: theme.palette.secondary.light,
    },
    '&:hover, &:focus': {
      cursor: 'pointer',
      color: theme.palette.primary.light,

      '& > svg': {
        color: theme.palette.primary.contrastText,
      },
    },

    '&:disabled': {
      color: theme.palette.secondary.main,
    },
  },
});

const CurrentImageTracker: React.FC<Props> = ({
  currentImageIndex,
  totalImages,
  onPreviousClick,
  onNextClick,
  activeImage,
}) => {
  const { currentlyRotatingImages, isConnectedToConnector } = useRotation();
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const showSegmentation = useAtomValue(showSegmentationAtom);
  const { mirroredImages } = useRotation();
  const isImageMirrored = activeImage?.imageId
    ? mirroredImages[activeImage.imageId] || false
    : false;
  const isImageRotated =
    activeImage &&
    currentlyRotatingImages?.imageIds.includes(activeImage.imageId);

  const classes = useStyles({ isImageRotated });
  const { t } = useLingui();
  const rerunAiButton = (isImageRotated || isImageMirrored) &&
    !isConnectedToConnector &&
    activeImage?.detectedImageType !== 'COLOR_IMAGE' && (
      <RerunAiButton currentImage={activeImage} />
    );
  if (currentImageIndex === -1) {
    return (
      <>
        {rerunAiButton}
        {selectedTeeth.length && (
          <div className={classes.imageNavigationBox}>
            <div className={classes.imageIndexText}>
              <ToothLabel />
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="vai-flex vai-items-center vai-justify-between vai-w-full vai-relative vai-min-h-10 vai-px-2">
      {/* Empty div acts as left-aligned flex child. */}
      <div />
      <div className="vai-flex vai-items-center vai-absolute vai-left-0 vai-right-0 vai-w-fit vai-mx-auto">
        {rerunAiButton}
        <div className={classes.imageNavigationBox}>
          <IconButton
            onClick={onPreviousClick}
            aria-label="Previous"
            className={classes.button}
            size="large"
          >
            <Tooltip title={t`Go to previous image`}>
              <ArrowBackIcon />
            </Tooltip>
          </IconButton>
          <div className={classes.imageIndexText}>
            <Typography>
              <span className={classes.imageFilterLabel}>
                <Trans>Images</Trans>
              </span>
              {currentImageIndex !== undefined
                ? currentImageIndex + 1
                : currentImageIndex}{' '}
              / {totalImages}
            </Typography>
            {!!selectedTeeth.length && <ToothLabel />}
            <ManageFindings />
          </div>

          <IconButton
            onClick={onNextClick}
            aria-label="Next"
            className={classes.button}
            size="large"
          >
            <Tooltip title={t`Go to next image`}>
              <ArrowForwardIcon />
            </Tooltip>
          </IconButton>
        </div>
      </div>
      {showSegmentation && (
        <p
          className="vai-font-inter vai-text-xs vai-text-zinc-500"
          data-testid="segmentation-disclaimer"
        >
          <Trans>Visualization is intended for patient education.</Trans>
        </p>
      )}
    </div>
  );
};

export default CurrentImageTracker;
